import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Close } from '@mui/icons-material';
import InputMask from 'react-input-mask';
import SystemAccessSelector from './SystemAccessSelector';
import BWITextField from '../shared/BWITextField';
import { Account } from '../../state/types';
import { bindActionCreators } from '@reduxjs/toolkit';
import { addAccount, deleteAccount, updateAccount, resendTempPassword } from '../../state/account/action_creator';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '../../state/reducers';
import RoleBasedAccess, { userHasAccess } from '../../services/RoleBasedAccess';

interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  account?: Account | null;
}

const roles = ["Admin", "Support", "Billing", "Auditor", "Client", "Client Plus", "Syndication Partner"];
const internalRoles = ["Admin", "Support", "Billing", "Auditor"];

const AccountModal: React.FC<AccountModalProps> = ({ open, onClose, account }) => {
  const user: Account = useSelector((state: State) => state.accountState.user);
  const dispatch = useDispatch()
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [resending, setResending] = useState(false);
  const addAccountAction = bindActionCreators(addAccount, dispatch)
  const updateAccountAction = bindActionCreators(updateAccount, dispatch)
  const deleteAccountAction = bindActionCreators(deleteAccount, dispatch)
  const resendTempPasswordAction = bindActionCreators(resendTempPassword, dispatch);
  const allSystems = useSelector((state: State) => state.systemState.systems);
  const isInternalUser = internalRoles.includes(role);

  useEffect(() => {
    if (account) {
      setEmail(account.email);
      setPhoneNumber(account.phone_number?.startsWith("+1") ? account.phone_number.slice(2) : account.phone_number || '');
      setFirstName(account.first_name || '');
      setLastName(account.last_name || '');
      setRole(account.role || '');
      setSelectedSystems(account.system_access || []);
    } else {
      setEmail('');
      setPhoneNumber('');
      setFirstName('');
      setLastName('');
      setRole('');
      setSelectedSystems([]);
    }
  }, [account]);

  useEffect(() => {
    if (allSystems?.length === 0) return;

    if (internalRoles.includes(role)) {
      const allSystemCodes = allSystems.map((sys: any) => sys.systemCode);
      setSelectedSystems(allSystemCodes);
    } else {
      setSelectedSystems([]);
    }
  }, [role, allSystems]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPhoneNumber('');
      setFirstName('');
      setLastName('');
      setRole('');
      setSelectedSystems([]);
    }
  }, [open]);

  const formatPhoneNumberToE164 = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  };

  const handleSystemSelection = (systemCode: string) => {
    setSelectedSystems((prev) =>
      prev.includes(systemCode) ? prev.filter((s) => s !== systemCode) : [...prev, systemCode]
    );
  };

  const handleAddOrUpdateAccount = async () => {
    if (!email) {
      alert('Please fill out email');
      return;
    }
    if (!firstName) {
      alert('Please fill out first name');
      return;
    }
    if (!lastName) {
      alert('Please fill out last name');
      return;
    }
    if (!phoneNumber) {
      alert('Please fill out phone number');
      return;
    }
    const newAccountData = {
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: formatPhoneNumberToE164(phoneNumber),
      role,
      system_access: selectedSystems
    };

    if (!account) {
      await addAccountAction(newAccountData);
    } else {
      await updateAccountAction({
        ...account,
        ...newAccountData
      }, user);
    }

    onClose()
  };

  const handleDeleteAccount = async () => {
    if (!account) return;

    await deleteAccountAction(account.username)
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      {open && (<>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {account ? 'Edit Account' : 'Add Account'}
          <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Left Column - User Info */}
            <Grid item xs={12} md={!userHasAccess(role, ['Internal']) ? 6 : 12} marginTop={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BWITextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth />
                <BWITextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth />
                <BWITextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
                  fullWidth />
                <InputMask mask="(999) 999-9999"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}>
                  {() => <BWITextField label="Phone Number" fullWidth />}
                </InputMask>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <FormControl fullWidth>
                    <InputLabel>
                      Role
                    </InputLabel>
                    <Select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      label="Role"
                      disabled={user?.role !== "Admin" && account?.role === "Admin"}
                    >
                      {roles.filter((r) => {
                        if (user?.role !== "Admin" && account?.role === "Admin") return true
                        if (r === "Admin") return user?.role === "Admin";
                        return true;
                      }).map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </RoleBasedAccess>
              </Box>
            </Grid>

            {/* Right Column - System Access */}
            {open && role && !userHasAccess(role, ['Internal']) && (
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Grid item xs={12} md={6} marginTop={2}>
                  <SystemAccessSelector
                    selectedSystems={selectedSystems}
                    onSystemSelect={handleSystemSelection}
                    disabled={isInternalUser}
                  />
                </Grid>
              </RoleBasedAccess>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {account ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                {((user?.role === "Admin") ||
                  ((user?.role === "Support") && (account?.role !== "Admin"))) &&
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {account?.status === "FORCE_CHANGE_PASSWORD" && (
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={resending}
                        onClick={async () => {
                          if (!account?.username) return;
                          setResending(true);
                          await resendTempPasswordAction(account.username);
                          setTimeout(() => setResending(false), 3000); // re-enable after 3s
                        }}
                      >
                        {resending ? "Sending..." : "Resend Temp Password"}
                      </Button>
                    )}



                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeleteAccount}
                    >
                      Delete
                    </Button>
                  </Box>
                }
              </RoleBasedAccess>
              <Box sx={{ marginLeft: 'auto' }}>
                <Button variant="contained" color="warning" onClick={handleAddOrUpdateAccount}>
                  Update
                </Button>
              </Box>
            </Box>
          ) : (
            <Button variant="contained" onClick={handleAddOrUpdateAccount} color="error">
              Sign Up
            </Button>
          )}
        </DialogActions>
      </>)}
    </Dialog>
  );
};

export default AccountModal;
